import styled, { css } from 'styled-components'
import { media } from 'styled-bootstrap-grid'

// Design tokens for the Entrepreneur Seminar program pages. `accent` resolves to
// theme.colors.primary (#46AECF); the neutrals below are specific to this design.
export const ink = '#111111'
export const inkSoft = '#1c1c1c'
export const body = '#555555'
export const muted = '#777777'
export const hairline = '#e8e8e8'
export const surface = '#f6f6f6'
export const tint = '#eaf6fa'
export const onDark = '#b3b3b3'
export const onDarkAccent = '#90cee2'
// ratings stay gold whatever the action colour is: a star reads as a score, never as a button
export const star = '#F5B301'
// credential badges on dark bands: white on the brand blue fails contrast, this is the lightest that passes
export const badgeBlue = '#2a7f9b'

// The ad page's call-to-action colour; buttons read it from --action, so other pages keep the brand blue
export const ACTION = { colour: '#0FA958', dark: '#0b8544' }
export const actionVars = css`
  --action: ${ACTION.colour};
  --action-dark: ${ACTION.dark};
`

export const accent = (props) => props.theme.colors.primary

// Styling props are transient ($-prefixed) so styled-components keeps them out of the DOM.

export const PageRoot = styled.div`
  font-family: 'Inter', -apple-system, 'Helvetica Neue', Arial, sans-serif;
  color: ${ink};
  background: #ffffff;
  overflow-x: hidden;
  -webkit-text-size-adjust: 100%;
  ${(props) => props.$action && actionVars}

  * {
    box-sizing: border-box;
  }

  a {
    color: ${accent};
    text-decoration: none;
  }

  a:hover {
    color: #3c94b0;
  }
`

// Shared gutter and vertical rhythm for every band on the page.
export const Section = styled.section`
  padding: 44px 16px;
  ${media.sm`padding: 56px 20px;`}
  ${media.lg`padding: 72px 24px;`}

  ${(props) => props.$dark && `background: ${ink}; color: #ffffff;`}
  ${(props) => props.$tinted && `background: ${surface};`}
  ${(props) => props.$flushTop && 'padding-top: 0;'}
  ${(props) => props.$tightTop && 'padding-top: 32px;'}
  ${(props) => props.$flushBottom && 'padding-bottom: 0;'}
  ${(props) => props.$tightBottom && 'padding-bottom: 40px;'}
`

export const Inner = styled.div`
  max-width: ${(props) => props.$maxWidth || 1120}px;
  margin: 0 auto;
`

export const Centered = styled.div`
  text-align: center;
  max-width: ${(props) => props.$maxWidth || 640}px;
  margin: 0 auto ${(props) => (props.$gap === undefined ? 48 : props.$gap)}px;
`

export const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${(props) => (props.$solid ? accent(props) : tint)};
  color: ${(props) => (props.$solid ? '#ffffff' : accent(props))};
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  margin-bottom: ${(props) => (props.$gap === undefined ? 20 : props.$gap)}px;
`

export const H1 = styled.h1`
  font-size: clamp(30px, 6vw, 52px);
  line-height: 1.12;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-wrap: balance;
  margin: 0 0 ${(props) => (props.$gap === undefined ? 24 : props.$gap)}px;
`

export const H2 = styled.h2`
  font-size: clamp(26px, 5vw, ${(props) => props.$max || 34}px);
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.2;
  text-wrap: balance;
  margin: 0 0 ${(props) => (props.$gap === undefined ? 18 : props.$gap)}px;
`

export const H3 = styled.h3`
  font-size: ${(props) => props.$size || 20}px;
  font-weight: 700;
  margin: 0 0 ${(props) => (props.$gap === undefined ? 12 : props.$gap)}px;
`

export const Lead = styled.p`
  font-size: ${(props) => props.$size || '16.5px'};
  line-height: 1.6;
  color: ${(props) => (props.$onDark ? onDark : body)};
  margin: ${(props) => props.$margin || '0'};
`

export const Card = styled.div`
  background: ${(props) => props.$bg || surface};
  border-radius: ${(props) => props.$radius || 20}px;
  padding: 22px;
  ${media.sm`padding: ${(props) => props.$padding || 28}px;`}
  ${(props) => props.$center && 'text-align: center;'}
`

export const buttonStyles = css`
  display: inline-block;
  padding: 16px 28px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.2;
  text-align: center;
  border: 1.5px solid transparent;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 150ms ease;

  ${(props) =>
    props.$variant === 'outline'
      ? css`
          border-color: ${hairline};
          background: transparent;
          color: ${ink};
        `
      : props.$variant === 'dark'
      ? css`
          background: ${ink};
          color: #ffffff;
        `
      : css`
          background: var(--action, ${accent(props)});
          color: #ffffff;
        `}

  ${(props) => props.$block && 'display: block; width: 100%;'}
  ${(props) => props.$gap && `margin-top: ${props.$gap}px;`}

  &:hover {
    opacity: 0.9;
    color: ${(props) => (props.$variant === 'outline' ? ink : '#ffffff')};
  }
`

export const Button = styled.button`
  ${buttonStyles}

  /* a registered interest reads as done, not as something to press again */
  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
`

// `PageRoot a` outranks a single class; `&&` keeps this anchor's own colours.
export const ButtonLink = styled.a`
  && {
    ${buttonStyles}
  }
`

export const Tag = styled.span`
  font-size: 11.5px;
  border: 1px solid ${(props) => (props.$onDark ? '#333333' : '#e0e0e0')};
  padding: 6px 12px;
  border-radius: 999px;
  color: ${(props) => (props.$onDark ? '#dddddd' : '#333333')};
`

export const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: ${(props) => (props.$gap === undefined ? 18 : props.$gap)}px;
`

// auto-fit grid; collapses to a single column once the tracks no longer fit
export const AutoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${(props) => props.$min || 240}px, 1fr));
  gap: ${(props) => props.$gap || 16}px;
`

export const Eyebrow = styled.div`
  font-size: 11.5px;
  letter-spacing: 0.06em;
  font-weight: 600;
  text-transform: uppercase;
  color: ${(props) => props.$color || accent(props)};
`
